/**
 * Authentication Service
 * Handles user authentication with backend RBAC via private Lambda
 */

import { PrivateLambdaService } from './privateLambdaService';
import { extractErrorMessage } from '../utils/errorHandler';

export interface LoginResponse {
  status: string;
  data: {
    token: string;
    user: {
      username: string;
      role: string;
      team?: string;
      actions: string[];
    };
  };
}

export interface VerifyTokenResponse {
  status: string;
  message: string;
}

export class AuthService {
  private privateLambdaService: PrivateLambdaService;
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';

  constructor() {
    this.privateLambdaService = new PrivateLambdaService();
  }

  /**
   * Login with user credentials
   * Calls backend /api/auth/login via signed Lambda request
   */
  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      console.log('Attempting login for user:', username);

      // Call backend login API via private Lambda with IAM auth
      const response = await this.privateLambdaService.callPrivateLambdaApi(
        '/api/auth/login',
        'POST',
        { username, password }
      );

      console.log('Login response:', response);

      // Handle different response formats
      let loginData: LoginResponse;
      
      if (response.status === 'success' && response.data) {
        loginData = response;
      } else if (response.body) {
        // Handle wrapped response
        const parsedBody = typeof response.body === 'string' 
          ? JSON.parse(response.body) 
          : response.body;
        loginData = parsedBody;
      } else {
        throw new Error('Invalid response format from login API');
      }

      // Store token and user data in sessionStorage
      this.storeAuthData(loginData.data.token, loginData.data.user);

      return loginData;
    } catch (error: any) {
      // Use centralized error handler to extract backend error message
      const errorMessage = extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  /**
   * Login with Azure AD token
   * Sends Azure id_token to backend for validation and JWT generation
   */
  async azureLogin(azureIdToken: string): Promise<LoginResponse> {
    try {
      console.log('Attempting Azure login with id_token');

      // Call backend login API with Azure token via private Lambda with IAM auth
      const response = await this.privateLambdaService.callPrivateLambdaApi(
        '/api/auth/login',
        'POST',
        { 
          isAzureLogin: true,
          azureToken: azureIdToken
        }
      );

      console.log('Azure login response:', response);

      // Handle different response formats
      let loginData: LoginResponse;
      
      if (response.status === 'success' && response.data) {
        loginData = response;
      } else if (response.body) {
        // Handle wrapped response
        const parsedBody = typeof response.body === 'string' 
          ? JSON.parse(response.body) 
          : response.body;
        loginData = parsedBody;
      } else {
        throw new Error('Invalid response format from Azure login API');
      }

      // Store token and user data in sessionStorage
      this.storeAuthData(loginData.data.token, loginData.data.user);

      return loginData;
    } catch (error: any) {
      // Use centralized error handler to extract backend error message
      const errorMessage = extractErrorMessage(error);
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Verify if current token is valid
   * Calls backend /api/auth/verify via signed Lambda request
   */
  async verifyToken(): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) {
        return false;
      }

      // Call backend verify API via private Lambda with IAM auth
      // Use custom header X-Auth-Token instead of Authorization header
      const headers = {
        'X-Auth-Token': `Bearer ${token}`
      };

      const response = await this.privateLambdaService.callPrivateLambdaApi(
        '/api/auth/verify',
        'GET',
        undefined,
        headers
      );

      console.log('Token verification response:', response);

      // Handle different response formats
      if (response.status === 'success' || response.message === 'Token is valid') {
        return true;
      }

      return false;
    } catch (error) {
      console.error('Token verification failed:', error);
      // Clear invalid token
      this.clearAuthData();
      return false;
    }
  }

  /**
   * Store authentication data in sessionStorage
   */
  private storeAuthData(token: string, user: any): void {
    sessionStorage.setItem(this.TOKEN_KEY, token);
    sessionStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Get stored authentication token
   */
  getToken(): string | null {
    return sessionStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Get stored user data
   */
  getUser(): any | null {
    const userData = sessionStorage.getItem(this.USER_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Clear authentication data from storage
   */
  clearAuthData(): void {
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
  }

  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Logout user - clear all auth data
   */
  logout(): void {
    this.clearAuthData();
  }
}

export default new AuthService();
